import type {
  AvailableOn,
  WeekDays,
} from 'app/components/commons/Experience/Availability/utils';

import type { ClosingPeriod, Experience } from './experiences';
import type { Bed, Picture } from './rooms';

export enum PackageStatus {
  PUBLISHED = 'published',
  UNPUBLISHED = 'unpublished',
  HIDDEN = 'hidden',
}

export type ExtranetPicture = {
  id: number;
  updatedAt: string;
  path: string;
  name: string;
  size: number;
  dimensions: {
    width: number;
    height: number;
  };
};

export type ExtranetExperience = {
  id: number;
  name: string | null;
  hotelId?: number;
  included: { [packageId: number]: boolean };
  price: number;
  priceDescription: string | null;
  categoryId: number;
  coverPicture: ExtranetPicture;
  emoji?: ExtranetPicture;
  closedHoursBeforeCheckin?: number;
  brand: string | null;
  quantity: number | null;
  duration: number | null;
  durationDescription: string | null;
  time: string;
  locations: string | null;
  maxQuantity: number | null;
  availableDays?: WeekDays[];
  availableDayLimits?: AvailableOn[];
  availableDates?: string[];
  published: boolean;
  closingPeriods?: ClosingPeriod[];
  hotelPriority?: number;
};

export type ExtranetRoom = {
  id: number;
  hotelId: number;
  categoryId: number;
  categoryName: string;
  name: string;
  beds: Bed[];
  bookingcomId?: number;
  bookingcomName?: string;
  categoryRoomCount?: number;
  coverPicture: ExtranetPicture;
  published: boolean;
  hotelPriority?: number;
};

export type ExtranetRoomForm = {
  id: number;
  beds: Bed[];
};

export type ExtranetPackageCore = {
  id: number;
  aliasForHotel: string;
  aliasForUser?: string;
  slug: string;
  published: boolean;
  dayPackage: boolean;
  singleCustomerDiscount?: number;
  additionalAdultPrice?: number;
  additionalChildPrice?: number;
  autoDiscountRate?: number;
  lastMinuteAutoDiscountRate?: number;
  childrenAllowed: boolean;
  defaultMaxBooking?: number;
  lastMinuteAutoDiscountDays?: number;
  coverLabel: string;
  minimumDiscountOverride?: number;
  minimumDiscountOverrideDates: Date[];
};

export type ExtranetPackageWithoutDetails = ExtranetPackageCore & {
  roomIds: number[];
  experienceIds: number[];
};

export type ExtranetPackage = ExtranetPackageCore & {
  rooms: ExtranetRoom[];
  experiences: ExtranetExperience[];
};

export type PackageListItem = {
  id: number;
  aliasForHotel: string;
  aliasForUser?: string;
  hotelName: string;
  hotelId: number;
  coverPicture: Picture;
};

export type Package = {
  coverPicture: Picture;
  description: string;
  experiences: Experience[];
  highlight: string;
  hotelId: number;
  id: number | null;
  mobileCoverPicture: Picture;
  name: string;
  rooms: number[];
  sliderPicture: Picture;
  cover: Picture;
  slug: string;
  dayPackage: boolean;
  singleCustomerDiscount?: number;
  additionalAdultPrice?: number;
  childrenAllowed?: boolean;
  aliasForHotel?: string;
  channelManagerRateCode?: string;
  minimumDiscountOverride?: number;
  minimumDiscountOverrideDates: Date[];
};

export type PackageCopywriting = {
  description: string;
  highlight: string;
  headlines: PackageCopywritingHeadline[];
  name: string;
  experienceTitle?: string;
  aliasForHotel?: string;
};

export type PackageCopywritingHeadline = {
  experienceId?: number;
  emoji: Picture;
  valueProposition: string;
  featured: boolean;
};

export type PackageForm = {
  id?: number;
  hotelId: number;
  slug: string;
  dayPackage: boolean;
  experiences: Experience[];
  rooms: number[];
  singleCustomerDiscount?: number;
  additionalAdultPrice?: number;
  minimumDiscountOverride?: number;
  minimumDiscountOverrideDates: Date[];
};

export type CopywritingForm = {
  experienceTitle?: string;
  highlight: string;
  description: string;
  headlines: {
    emoji: Picture;
    valueProposition: string;
    experienceId: number;
    featured: boolean;
  }[];
};

export type PackageDiscover = {
  introPictures: Picture[];
  roomPictures: Picture[];
  packageExperiences: (Experience & { discover: boolean })[];
};

export type PackageCover = {
  coverLabel1?: string;
  coverLabel2?: string;
  horizontalCoverPicture: Picture;
  verticalCoverPicture: Picture;
  /**
   * @deprecated use one of horizontalCoverPicture or verticalCoverPicture instead
   */
  coverPicture: Picture;
  /**
   * @deprecated use one of horizontalCoverPicture or verticalCoverPicture instead
   */
  appPicture: Picture;
  /**
   * @deprecated use one of horizontalCoverPicture or verticalCoverPicture instead
   */
  sliderPicture: Picture;
};

export enum DisclaimerKind {
  ADVICE = 'ADVICE',
  ALERT = 'ALERT',
}

export type PackageDisclaimer = {
  id: number | null;
  kind: DisclaimerKind;
  text: string;
  startDate: string | null;
  endDate: string | null;
  packageId: number;
  temporaryId: number;
  priority: number;
};

export type PackageDisclaimerWithoutTemporaryId = Omit<
  PackageDisclaimer,
  'temporaryId'
>;

export type PackageCore = {
  id: number;
  createdAt: string;
  updatedAt: string;
  /** @deprecated */
  name: string;
  aliasForHotel: string;
  aliasForUser?: string;
  slug: string;
  preview: string;
  description: string;
  published: boolean;
  publishedAt?: string;
  deletedAt?: string;
  coverPictureId: number;
  discoverTitle?: string;
  discoverDescription?: string;
  computedDiscountPrice?: number;
  sliderPictureId: number;
  hotelId: number;
  highlight: string;
  pageTitle?: string;
  mobileCoverPictureId: number;
  valueProposition?: string;
  hidden: boolean;
  hiddenAt?: string;
  dayPackage: boolean;
  rankingScore: number;
  singleCustomerDiscount?: number;
  additionalAdultPrice?: number;
  additionalChildPrice?: number;
  coverLabel1?: string;
  coverLabel2?: string;
  horizontalCoverPictureId: number;
  verticalCoverPictureId: number;
  childrenAllowed: boolean;
  autoDiscountRate?: number;
  autoDiscountActivatedAt?: string;
  autoDiscountDeactivatedAt?: string;
  lastMinuteAutoDiscountRate?: number;
  lastMinuteAutoDiscountDays?: number;
  lastMinuteAutoDiscountActivatedAt?: string;
  lastMinuteAutoDiscountDeactivatedAt?: string;
  defaultMaxBooking?: number;
  oldHotelId?: number;
};
